<template>
  <div
    id="header"
    :class="[
      headerClass,
      isSummaryPage
        ? 'border-b border-solid border-onelaunch_ui_shades_300 dark:border-border_header_summary'
        : '',
    ]"
    class="bg-onelaunch_ui_shades_50 dark:bg-radio_group_text_selected_dark"
  >
    <div id="header-content" :class="headerClass + '__content'">
      <div :class="headerClass + '__brand-logo'">
        <NuxtLink to="/">
          <ShiftLogo v-if="browser === 'SHIFT'" :class="headerClass + '__logo-image'" />
          <OneNewsLogo v-else :class="headerClass + '__logo-image'" />
        </NuxtLink>
      </div>
      <div v-if="!isSummaryPage" :class="headerClass + '__searchbox'">
        <ClassicNtpCommonSearchbox :enhanced-searchbox="true" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { storeToRefs } from 'pinia'
  import { useNewsToggleStore } from '~/store/NewsToggle'
  import OneNewsLogo from '~/assets/icons/one_news_logo.svg'
  import ShiftLogo from '~/assets/images/shift-logo.svg'

  const props = defineProps({
    isSummaryPage: {
      type: Boolean,
      default: false,
    },
    isStickyHeader: {
      type: Boolean,
      default: true,
    },
  })

  const browser = useBrowser()
  const newsToggleStore = useNewsToggleStore()
  const { newsToggle } = storeToRefs(newsToggleStore)

  const headerClass = ref('header')
  let admTiles

  function setClassHiddenNews(data) {
    if (props.isSummaryPage) {
      return
    }
    if (!data) {
      headerClass.value = window.innerWidth >= 1920 ? 'header-news-hidden' : 'header-news-hidden-small-layout'
      document.documentElement.style.scrollbarGutter = 'stable'

      admTiles = document.getElementById('tiles')
      if (admTiles) {
        admTiles.style.marginTop = '250px'
      }
    } else {
      headerClass.value = window.innerWidth >= 1920 ? 'header-1920' : 'header'
      document.documentElement.style.removeProperty('scrollbar-gutter')

      admTiles = document.getElementById('tiles')
      if (admTiles) {
        admTiles.style.marginTop = '0px'
      }
    }
  }

  watch(newsToggle, (newVal) => {
    if (process.client) {
      setClassHiddenNews(newVal)
    }
  })

  const fixedClass = headerClass.value + (props.isStickyHeader ? '--fixed' : '')
  let header
  let threshold

  onMounted(() => {
    document.addEventListener('scroll', handleScroll)

    if (props.isSummaryPage) {
      headerClass.value = 'header-summary'
    } else if (window.innerWidth >= 1920) {
      headerClass.value = 'header-1920'
    }
    if (!newsToggle.value && newsToggle.value !== undefined) {
      setClassHiddenNews(newsToggle.value)
    }

    header = document.querySelector('#header')
    threshold = header?.getBoundingClientRect()
  })

  onUnmounted(() => {
    document.removeEventListener('scroll', handleScroll)
  })

  let searchboxUpdating = false

  function handleSearchbox() {
    if (threshold === undefined) {
      header = document.querySelector('#header')
      threshold = header?.getBoundingClientRect()
    }

    if (window.scrollY > threshold.top || window.pageYOffset > threshold.top) {
      header.classList.add(fixedClass)

      admTiles = document.querySelector('#tiles')
      if (admTiles) {
        admTiles.style.marginTop = document.querySelector('#header').offsetHeight + 'px'
      }
    } else if (newsToggle.value) {
      admTiles = document.querySelector('#tiles')
      if (admTiles) {
        admTiles.style.marginTop = '0px'
      }
      header.classList.remove(fixedClass)
    }
    searchboxUpdating = false
  }

  function handleScroll() {
    if (!searchboxUpdating) {
      searchboxUpdating = true
      window.requestAnimationFrame(handleSearchbox)
    }
  }
</script>

<style lang="scss" scoped>
  .header-summary--fixed {
    position: fixed;
    top: 0;
    z-index: 100;
    left: 0;
    right: 0;
  }

  .header--fixed,
  .header-1920--fixed {
    position: fixed;
    top: 0;
    z-index: 100;
    left: 0;
    right: 0;
    border-bottom: 1px solid #eaecee;
  }

  .header-news-hidden-small-layout,
  .header-news-hidden {
    width: 100%;
    height: 96px;
    display: block;
    margin: 0 auto;
    padding: 22px 0;

    &__content {
      max-width: 1596px;
      width: 100%;
      display: inline-grid;
      height: 52px;
      grid-template-columns: 1fr 784px 1fr;
      grid-template-rows: 500px;
    }

    &__brand-logo {
      max-width: 209px;
      width: 100%;
      justify-self: start;
      margin-top: 14px;
    }

    &__searchbox {
      max-width: 784px;
      width: 100%;
      align-self: center;
    }
  }

  .header-news-hidden-small-layout__content {
    max-width: 1272px;
  }

  .header,
  .header-1920 {
    width: 100%;
    height: 96px;
    display: block;
    margin: 0 auto;
    padding: 22px 0;

    &__content {
      max-width: 1596px;
      width: 100%;
      display: inline-grid;
      height: 52px;
      align-items: center;
      grid-template-columns: 1fr 784px 1fr;
    }

    &__brand-logo {
      max-width: 209px;
      width: 100%;
      justify-self: start;
    }

    &__searchbox {
      max-width: 784px;
      width: 100%;
    }
  }

  .header-summary {
    width: 100%;
    height: 96px;
    display: block;
    margin: 0 auto;
    padding: 22px 0;

    &__content {
      max-width: 1152px;
      width: 100%;
      display: inline-grid;
      height: 52px;
      align-items: center;
      grid-template-columns: 209px auto 209px;
    }

    &__brand-logo {
      max-width: 209px;
      width: 100%;
      justify-self: start;
    }

    &__searchbox {
      max-width: 784px;
      width: 100%;
    }
  }

  .header__content {
    max-width: 1272px;
  }

  @media (max-width: 1250px) {
    .header-summary {
      &__brand-logo {
        max-width: 110px;
      }

      &__content {
        max-width: 784px;
        grid-template-columns: 120px auto 120px;
      }
    }
  }

  @media (max-width: 900px) {
    .header-summary {
      &__content {
        max-width: 650px;
      }
    }
  }

  @media (max-width: 750px) {
    .header-summary {
      height: 140px;

      &__content {
        max-width: 450px;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 48px 48px;
        grid-row-gap: 10px;
      }

      &__searchbox {
        justify-self: center;
        grid-row-start: 2;
        grid-column: span 2;
      }
    }
  }

  @media (max-width: 550px) {
    .header-summary {
      max-width: 362px;
    }
  }

  @media (max-width: 1360px) {
    .header {
      &__content {
        max-width: 948px;
        grid-template-columns: 1fr 680px 1fr;
      }
    }

    .header-news-hidden-small-layout {
      &__content {
        max-width: 948px;
        grid-template-columns: 1fr 680px 1fr;
      }
    }
  }

  @media (max-width: 1034px) {
    .header {
      height: 140px;

      &__content {
        max-width: 624px;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 48px 48px;
        grid-row-gap: 10px;
      }

      &__searchbox {
        justify-self: center;
        grid-row-start: 2;
        grid-column: span 2;
      }
    }

    .header-news-hidden-small-layout {
      &__content {
        max-width: 624px;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 48px 48px;
        grid-row-gap: 180px;
      }
      &__searchbox {
        justify-self: center;
        grid-row-start: 2;
        grid-column: span 2;
      }
      &__brand-logo {
        margin-top: 12px;
      }
    }
  }

  @media (max-width: 772px) {
    .header {
      &__content {
        max-width: 300px;
        grid-template-columns: 1fr 1fr;
        padding: 0 8px;
      }
    }
  }

  @media (max-width: 1860px) {
    .header-1920,
    .header-news-hidden {
      &__content {
        max-width: 1272px !important;
      }
    }
  }

  @media (max-width: 1506px) {
    .header-1920,
    .header-news-hidden {
      &__content {
        max-width: 948px !important;
        grid-template-columns: 1fr 700px 1fr;
      }
    }
  }

  @media (max-width: 1145px) {
    .header-1920 {
      &__searchbox {
        max-width: 400px;
      }

      &__brand-logo {
        padding-right: 8px;
      }

      &__content {
        max-width: 624px !important;
        grid-template-columns: 1fr 400px 1fr;
      }
    }

    .header-news-hidden {
      &__searchbox {
        max-width: 484px;
      }

      &__content {
        max-width: 696px !important;
        grid-template-columns: 1fr 484px 1fr;
      }
    }
  }

  @media (max-width: 772px) {
    .header-1920 {
      height: 140px;

      &__content {
        max-width: 300px !important;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 48px 48px;
        grid-row-gap: 10px;
        padding: 0 8px;
      }

      &__searchbox {
        justify-self: center;
        grid-row-start: 2;
        grid-column: span 2;
      }
    }

    .header-news-hidden-small-layout {
      &__content {
        max-width: 300px !important;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 48px 300px;
        grid-row-gap: 0;
        padding: 0 8px;
      }

      &__searchbox {
        justify-self: center;
        grid-row-start: 2;
        grid-column: span 2;
      }
    }

    .tiles {
      height: auto;
    }
  }

  .dark .header--fixed,
  .dark .header-1920--fixed {
    border-bottom: 1px solid #9d9ea11a;
  }
</style>
